/**
 * Free Quote — suburb picker (offline, data-driven).
 *
 * Turns the plain #city text input into a searchable combobox over the static
 * suburb lists in static/data/suburbs/AU-<STATE>.json. Two modes, chosen by the
 * wrapper's attributes:
 *
 * STATE-FIRST (no `data-suburb-all`) — v1's shape.
 *   - #state empty            → #city disabled with a "select a state" hint.
 *   - #state chosen           → that state's suburb list is fetched and #city
 *                               becomes a type-to-filter / scroll combobox.
 *   - pick a suburb           → #city = suburb name, #zip_code = its postcode.
 *
 * ALL-STATES (`data-suburb-all="NSW,VIC,…"`) — v2's shape, where #state and
 * #zip_code are hidden and the suburb is the only address field on screen.
 *   - every listed state's file is fetched once, on the first focus or
 *     keystroke, and merged into one alphabetical list. Options carry their
 *     state so the same suburb name in two states stays distinguishable.
 *   - pick a suburb           → #city, #zip_code AND #state are all filled.
 *   - #state is never read as an input here: it is an output.
 *
 * In both modes the fills fire input+change so validation/summary stay in sync.
 *
 * FIELD IDS are the wrapper's to declare, so a form that names its input
 * something else can still use this picker:
 *
 *   data-suburb-input     id of the suburb text input   (default `city`)
 *   data-suburb-state     id of the state control       (default `state`)
 *   data-suburb-postcode  id of the postcode input      (default `zip_code`)
 *
 * Only the input is required. State and postcode are *outputs* in all-states
 * mode, so a form with neither — the contact enquiry form, which asks for a
 * suburb and nothing else about the address — gets the same picker and simply
 * has nowhere for them to land. State-first mode still needs the state control,
 * since that is what chooses the list.
 *
 * Degrades gracefully: if the fetch fails or JS is off, #city is still a normal
 * required text input the customer can type into. This script is only loaded
 * when the Google Places autocomplete is disabled (they both enhance #city), so
 * the two never fight over the same field.
 */

(function () {
    'use strict';

    // How many options to paint at once. AU states have thousands of suburbs;
    // rendering all of them on every keystroke janks the main thread, so we cap
    // the visible list and tell the user to keep typing when it's truncated.
    var MAX_RESULTS = 60;

    function ready(fn) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', fn);
        } else {
            fn();
        }
    }

    function fireChange(el) {
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function escapeHtml(s) {
        return String(s || '')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;')
            .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    ready(function init() {
        var wrap = document.getElementById('suburb-combobox-wrap');
        var dropdown = document.getElementById('suburb-combobox-dropdown');
        if (!wrap || !dropdown) return;

        // The field ids are the wizard's by default, but a form that asks for a
        // suburb and nothing else (the contact enquiry form) names the input
        // after what it is and has no state or postcode field to write to. The
        // ids are therefore the wrapper's to declare.
        function byId(attr, fallback) {
            return document.getElementById(wrap.getAttribute(attr) || fallback);
        }
        var cityInput = byId('data-suburb-input', 'city');
        var stateSelect = byId('data-suburb-state', 'state');
        var zipInput = byId('data-suburb-postcode', 'zip_code');
        if (!cityInput) return;

        var srcBase = wrap.getAttribute('data-suburb-src') || '/static/data/suburbs/';
        if (srcBase.charAt(srcBase.length - 1) !== '/') srcBase += '/';

        // All-states mode: the states to merge, in the order they load. Empty
        // (attribute absent) keeps the original state-first behaviour.
        var allStates = (wrap.getAttribute('data-suburb-all') || '')
            .split(',').map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean);
        var allMode = allStates.length > 0;
        // State-first mode *is* the state dropdown — without it there is no list
        // to pick from. All-states mode only ever writes to it, so a form with no
        // state field still works there.
        if (!allMode && !stateSelect) return;
        var allLoaded = false;      // the merged list is in hand
        var allLoading = null;      // in-flight merge, so focus+input don't double-fetch

        var cache = {};        // stateCode -> [[name, postcode], ...]
        var loadingState = null;
        var currentList = [];  // active state's suburb list
        var filtered = [];     // options currently rendered
        var activeIndex = -1;  // keyboard-highlighted option
        var postcodeIndex = null;   // { postcode: state | [state,...] } once loaded
        var suppressStateClear = false;  // set while we drive #state from a postcode
        var internalUpdate = false;      // set while we fill city/zip from a picked suburb
                                         // (stops the field events from re-opening the list)

        function setCityEnabled(on, placeholder) {
            cityInput.disabled = !on;
            cityInput.placeholder = placeholder;
            if (!on) {
                cityInput.classList.add('opacity-60', 'cursor-not-allowed');
            } else {
                cityInput.classList.remove('opacity-60', 'cursor-not-allowed');
            }
        }

        function openDropdown() { dropdown.classList.remove('hidden'); cityInput.setAttribute('aria-expanded', 'true'); }
        function closeDropdown() {
            dropdown.classList.add('hidden');
            cityInput.setAttribute('aria-expanded', 'false');
            activeIndex = -1;
        }

        function renderOptions(query) {
            var q = (query || '').trim().toLowerCase();
            var out = [];
            for (var i = 0; i < currentList.length && out.length < MAX_RESULTS + 1; i++) {
                var name = currentList[i][0];
                var pc = currentList[i][1];
                if (!q || name.toLowerCase().indexOf(q) !== -1 || pc.indexOf(q) === 0) {
                    out.push(currentList[i]);
                }
            }
            var truncated = out.length > MAX_RESULTS;
            filtered = truncated ? out.slice(0, MAX_RESULTS) : out;
            activeIndex = -1;

            if (filtered.length === 0) {
                dropdown.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">No matching suburb. Check the spelling or type your postcode.</div>';
                openDropdown();
                return;
            }

            var html = filtered.map(function (row, idx) {
                // row[2] is the state code, present in all-states mode only. It
                // is what tells "Richmond VIC 3121" from "Richmond NSW 2753",
                // and it is carried on the option so the pick can set #state.
                var meta = (row[2] ? escapeHtml(row[2]) + ' ' : '') + escapeHtml(row[1]);
                return '<button type="button" role="option" data-idx="' + idx + '" ' +
                    'data-name="' + escapeHtml(row[0]) + '" data-postcode="' + escapeHtml(row[1]) + '" ' +
                    'data-state="' + escapeHtml(row[2] || '') + '" ' +
                    'class="suburb-option w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none">' +
                    '<span class="truncate">' + escapeHtml(row[0]) + '</span>' +
                    '<span class="shrink-0 text-xs text-gray-400 tabular-nums">' + meta + '</span>' +
                    '</button>';
            }).join('');
            if (truncated) {
                html += '<div class="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">Showing first ' +
                    MAX_RESULTS + ' — keep typing to narrow it down.</div>';
            }
            dropdown.innerHTML = html;
            openDropdown();
        }

        function loadState(code) {
            if (!code) { currentList = []; return Promise.resolve(); }
            if (cache[code]) { currentList = cache[code]; return Promise.resolve(); }
            loadingState = code;
            setCityEnabled(false, 'Loading suburbs…');
            return fetch(srcBase + 'AU-' + encodeURIComponent(code) + '.json', { credentials: 'same-origin' })
                .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
                .then(function (data) {
                    cache[code] = Array.isArray(data) ? data : [];
                    if (loadingState === code) {
                        currentList = cache[code];
                        setCityEnabled(true, 'Start typing your suburb…');
                    }
                })
                .catch(function () {
                    // Fetch failed → fall back to a plain typeable field so the
                    // customer is never blocked from entering their suburb.
                    cache[code] = [];
                    currentList = [];
                    if (loadingState === code) setCityEnabled(true, 'Enter your suburb');
                });
        }

        /**
         * All-states mode: fetch every state's list once and merge them into one
         * alphabetical array of [name, postcode, state]. A state whose file
         * fails is simply missing from the list — the field stays typeable, so
         * the customer is never blocked.
         */
        function loadAllStates() {
            if (allLoaded) return Promise.resolve();
            if (allLoading) return allLoading;

            // Deliberately NOT disabled while it loads. The fetch is triggered by
            // the customer's own focus or first keystroke, and taking the field
            // away from them at that exact moment swallowed what they were
            // typing — on a phone it closed the keyboard mid-word. The dropdown
            // says what is happening instead, and the list renders against
            // whatever they typed while waiting.
            dropdown.innerHTML =
                '<div class="px-4 py-3 text-sm text-gray-500">Loading suburbs…</div>';
            openDropdown();

            allLoading = Promise.all(allStates.map(function (code) {
                return fetch(srcBase + 'AU-' + encodeURIComponent(code) + '.json', { credentials: 'same-origin' })
                    .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
                    .then(function (data) {
                        return (Array.isArray(data) ? data : []).map(function (row) {
                            return [row[0], row[1], code];
                        });
                    })
                    .catch(function () { return []; });
            })).then(function (lists) {
                var merged = [];
                lists.forEach(function (list) { merged = merged.concat(list); });
                merged.sort(function (a, b) {
                    if (a[0] === b[0]) return a[2] < b[2] ? -1 : 1;
                    return a[0] < b[0] ? -1 : 1;
                });
                currentList = merged;
                allLoaded = true;
                allLoading = null;
                // Placeholder only — the field was never disabled, so this is
                // not re-enabling it.
                cityInput.placeholder = merged.length
                    ? 'Start typing your suburb or postcode…'
                    : 'Enter your suburb';
            });
            return allLoading;
        }

        function selectRow(row) {
            // Guard the whole fill so the resulting input/focus events (and the
            // postcode reverse-lookup the zip 'input' would otherwise trigger)
            // don't re-open the dropdown we're about to close.
            internalUpdate = true;
            cityInput.value = row[0];
            fireChange(cityInput);
            if (zipInput && row[1]) {
                zipInput.value = row[1];
                fireChange(zipInput);
            }
            // All-states mode: the pick is also what decides the state. Guarded
            // by suppressStateClear so the change handler below doesn't read it
            // as a user switching states and wipe the suburb we just set.
            if (stateSelect && row[2] && stateSelect.value !== row[2]) {
                suppressStateClear = true;
                stateSelect.value = row[2];
                fireChange(stateSelect);
                suppressStateClear = false;
            }
            closeDropdown();
            cityInput.focus();
            internalUpdate = false;
        }

        function setActive(idx) {
            var opts = dropdown.querySelectorAll('.suburb-option');
            if (!opts.length) return;
            if (idx < 0) idx = opts.length - 1;
            if (idx >= opts.length) idx = 0;
            opts.forEach(function (o) { o.classList.remove('is-active', 'bg-gray-100'); });
            var el = opts[idx];
            el.classList.add('is-active', 'bg-gray-100');
            el.scrollIntoView({ block: 'nearest' });
            activeIndex = idx;
        }

        // --- events ---------------------------------------------------------

        if (stateSelect) stateSelect.addEventListener('change', function () {
            // In all-states mode #state is an output of the pick, never an
            // input to it — there is no per-state list to swap and nothing to
            // clear.
            if (allMode) return;
            var code = stateSelect.value;
            // Changing state invalidates the previously-picked suburb + postcode —
            // unless we're the ones setting the state from a typed postcode, in
            // which case the postcode (and soon the suburb) must be preserved.
            if (!suppressStateClear) {
                cityInput.value = '';
                if (zipInput) zipInput.value = '';
            }
            closeDropdown();
            if (!code) {
                currentList = [];
                setCityEnabled(false, 'Select a state first');
                return;
            }
            var p = loadState(code);
            if (!suppressStateClear && p && p.then) { p.then(function () { cityInput.focus(); }); }
        });

        cityInput.addEventListener('focus', function () {
            if (cityInput.disabled || internalUpdate) return;
            if (allMode && !allLoaded) {
                loadAllStates().then(function () { if (document.activeElement === cityInput) renderOptions(cityInput.value); });
                return;
            }
            if (currentList.length) renderOptions(cityInput.value);
        });

        cityInput.addEventListener('input', function () {
            if (cityInput.disabled || internalUpdate) return;
            if (allMode && !allLoaded) {
                loadAllStates().then(function () { renderOptions(cityInput.value); });
                return;
            }
            if (currentList.length) renderOptions(cityInput.value);
        });

        cityInput.addEventListener('keydown', function (e) {
            if (dropdown.classList.contains('hidden')) {
                if (e.key === 'ArrowDown') {
                    if (allMode && !allLoaded) { loadAllStates().then(function () { renderOptions(cityInput.value); }); }
                    else if (currentList.length) { renderOptions(cityInput.value); }
                }
                return;
            }
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
            else if (e.key === 'Enter') {
                if (activeIndex >= 0 && filtered[activeIndex]) { e.preventDefault(); selectRow(filtered[activeIndex]); }
            } else if (e.key === 'Escape') { closeDropdown(); }
        });

        dropdown.addEventListener('mousedown', function (e) {
            // mousedown (not click) so it beats the input's blur/close.
            var btn = e.target.closest('.suburb-option');
            if (!btn) return;
            e.preventDefault();
            selectRow([
                btn.getAttribute('data-name'),
                btn.getAttribute('data-postcode'),
                btn.getAttribute('data-state') || '',
            ]);
        });

        document.addEventListener('click', function (e) {
            if (!wrap.contains(e.target)) closeDropdown();
        });

        // --- reverse lookup: postcode -> state (+ narrow the suburb) --------

        function loadIndex() {
            if (postcodeIndex) return Promise.resolve(postcodeIndex);
            return fetch(srcBase + 'postcode-index.json', { credentials: 'same-origin' })
                .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
                .then(function (data) { postcodeIndex = data || {}; return postcodeIndex; })
                .catch(function () { postcodeIndex = {}; return postcodeIndex; });
        }

        // Resolve which state a postcode belongs to. Border postcodes hold an
        // array (most-common first); keep the currently-selected state if it's a
        // valid option, otherwise take the most likely one.
        function resolveState(entry) {
            if (!entry) return null;
            if (Array.isArray(entry)) {
                if (stateSelect && stateSelect.value && entry.indexOf(stateSelect.value) !== -1) return stateSelect.value;
                return entry[0];
            }
            return entry;
        }

        function applyPostcode(pc) {
            loadIndex().then(function (idx) {
                var target = resolveState(idx[pc]);
                if (!target) return;   // unknown postcode — leave everything as typed

                function narrowSuburb() {
                    var matches = currentList.filter(function (row) { return row[1] === pc; });
                    if (matches.length === 1) {
                        // Unambiguous → fill the suburb outright.
                        cityInput.value = matches[0][0];
                        fireChange(cityInput);
                        closeDropdown();
                    } else if (matches.length > 1) {
                        // Several suburbs share this postcode → open the list
                        // pre-filtered to them so the customer just picks one.
                        cityInput.focus();
                        renderOptions(pc);
                    }
                }

                if (stateSelect && stateSelect.value !== target) {
                    suppressStateClear = true;
                    stateSelect.value = target;
                    // Notify any other listeners (validation/summary) without our
                    // own handler wiping the postcode the user just typed.
                    stateSelect.dispatchEvent(new Event('change', { bubbles: true }));
                    var p = loadState(target);
                    (p && p.then ? p : Promise.resolve()).then(function () {
                        suppressStateClear = false;
                        narrowSuburb();
                    });
                } else {
                    loadState(target);
                    narrowSuburb();
                }
            });
        }

        if (zipInput && !allMode) {
            zipInput.addEventListener('input', function () {
                // Skip when we're filling the postcode from a picked suburb — the
                // reverse-lookup would otherwise re-open the just-closed dropdown.
                if (internalUpdate) return;
                var pc = (zipInput.value || '').trim();
                if (/^\d{4}$/.test(pc)) applyPostcode(pc);
            });
        }

        // --- initial state --------------------------------------------------
        if (allMode) {
            // Typeable from the start; the ~400KB of suburb data is deferred to
            // the first focus/keystroke so a visitor who never reaches the last
            // step never pays for it.
            setCityEnabled(true, cityInput.placeholder || 'Start typing your suburb or postcode…');
        } else if (stateSelect && stateSelect.value) {
            var p0 = loadState(stateSelect.value);
            // If a state was pre-detected, keep whatever #city the server rendered.
        } else {
            setCityEnabled(false, 'Select a state first');
        }
    });
})();
